import { FFmpeg } from "@ffmpeg/ffmpeg";
import { fetchFile } from "@ffmpeg/util";
import { isMp4Video } from "./videoMimeUtils";

const FFMPEG_CORE_VERSION = "0.12.10";
const FFMPEG_CORE_PATH = "dist/esm";

const FFMPEG_CORE_CDN_BASES = [
  `https://unpkg.com/@ffmpeg/core@${FFMPEG_CORE_VERSION}/${FFMPEG_CORE_PATH}`,
  `https://cdn.jsdelivr.net/npm/@ffmpeg/core@${FFMPEG_CORE_VERSION}/${FFMPEG_CORE_PATH}`,
] as const;

const FFMPEG_FETCH_RETRIES = 2;
const FFMPEG_CACHE_DB = "twist-ffmpeg-cache";
const FFMPEG_CACHE_STORE = "assets";
/** Bumped when cache validation rules change — invalidates poisoned HTML entries. */
const FFMPEG_CACHE_KEY_PREFIX = `${FFMPEG_CORE_VERSION}-v2`;

const FFMPEG_CORE_JS_SIZE = 112_000;
const FFMPEG_CORE_WASM_SIZE = 32_232_419;

export type ExportProgressCallback = (percent: number) => void;

export type GetFfmpegInstanceOptions = {
  onLoadProgress?: ExportProgressCallback;
};

let ffmpegInstance: FFmpeg | null = null;
let ffmpegLoadPromise: Promise<FFmpeg> | null = null;
let loadProgressListeners: ExportProgressCallback[] = [];

const notifyLoadProgress = (percent: number): void => {
  for (const listener of loadProgressListeners) {
    listener(percent);
  }
};

export const isFfmpegReady = (): boolean => !!ffmpegInstance?.loaded;

const getFfmpegAssetBases = (): readonly string[] => {
  if (import.meta.env.DEV) {
    return ["/ffmpeg-core", ...FFMPEG_CORE_CDN_BASES];
  }
  return FFMPEG_CORE_CDN_BASES;
};

const isValidFfmpegJsBlob = async (blob: Blob): Promise<boolean> => {
  if (blob.size < 1_000) return false;
  const head = new TextDecoder().decode(
    await blob.slice(0, 64).arrayBuffer(),
  ).trimStart();
  if (head.startsWith("<")) return false;
  return head.includes("createFFmpegCore") || head.startsWith("var");
};

const isValidFfmpegWasmBlob = async (blob: Blob): Promise<boolean> => {
  if (blob.size < 1_000_000) return false;
  const magic = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return (
    magic[0] === 0x00 &&
    magic[1] === 0x61 &&
    magic[2] === 0x73 &&
    magic[3] === 0x6d
  );
};

const isValidFfmpegAssetBlob = async (
  fileName: string,
  blob: Blob,
): Promise<boolean> => {
  if (fileName.endsWith(".wasm")) return isValidFfmpegWasmBlob(blob);
  return isValidFfmpegJsBlob(blob);
};

const cacheKeyFor = (fileName: string): string =>
  `${FFMPEG_CACHE_KEY_PREFIX}/${fileName}`;

const openCacheDb = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(FFMPEG_CACHE_DB, 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(FFMPEG_CACHE_STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

const getCachedAsset = async (fileName: string): Promise<Blob | null> => {
  try {
    const db = await openCacheDb();
    const key = cacheKeyFor(fileName);
    return new Promise((resolve, reject) => {
      const tx = db.transaction(FFMPEG_CACHE_STORE, "readonly");
      const req = tx.objectStore(FFMPEG_CACHE_STORE).get(key);
      req.onsuccess = () => resolve((req.result as Blob | undefined) ?? null);
      req.onerror = () => reject(req.error);
      tx.oncomplete = () => db.close();
    });
  } catch {
    return null;
  }
};

const deleteCachedAsset = async (fileName: string): Promise<void> => {
  try {
    const db = await openCacheDb();
    const key = cacheKeyFor(fileName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FFMPEG_CACHE_STORE, "readwrite");
      tx.objectStore(FFMPEG_CACHE_STORE).delete(key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Cache delete failures are non-fatal
  }
};

const setCachedAsset = async (fileName: string, blob: Blob): Promise<void> => {
  try {
    const db = await openCacheDb();
    const key = cacheKeyFor(fileName);
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FFMPEG_CACHE_STORE, "readwrite");
      tx.objectStore(FFMPEG_CACHE_STORE).put(blob, key);
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    // Cache write failures are non-fatal
  }
};

const fetchToBlob = async (
  url: string,
  mimeType: string,
  onBytes?: (received: number, total: number | null) => void,
): Promise<Blob> => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`HTTP ${response.status} for ${url}`);
  }

  const totalHeader = response.headers.get("Content-Length");
  const total = totalHeader ? Number(totalHeader) : null;
  const reader = response.body?.getReader();

  if (!reader) {
    const blob = await response.blob();
    onBytes?.(blob.size, total ?? blob.size);
    return new Blob([blob], { type: mimeType });
  }

  const chunks: BlobPart[] = [];
  let received = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    onBytes?.(received, total);
  }

  return new Blob(chunks, { type: mimeType });
};

const blobToObjectURL = (blob: Blob, mimeType: string): string =>
  URL.createObjectURL(new Blob([blob], { type: mimeType }));

const fetchFfmpegAsset = async (
  fileName: string,
  mimeType: string,
  onBytes?: (received: number, total: number | null) => void,
): Promise<Blob> => {
  const cached = await getCachedAsset(fileName);
  if (cached) {
    if (await isValidFfmpegAssetBlob(fileName, cached)) {
      onBytes?.(cached.size, cached.size);
      return cached;
    }
    await deleteCachedAsset(fileName);
  }

  const errors: string[] = [];

  for (const base of getFfmpegAssetBases()) {
    for (let attempt = 0; attempt < FFMPEG_FETCH_RETRIES; attempt++) {
      try {
        const blob = await fetchToBlob(`${base}/${fileName}`, mimeType, onBytes);
        if (!(await isValidFfmpegAssetBlob(fileName, blob))) {
          throw new Error(
            `Invalid ${fileName} payload from ${base} (HTML or truncated response)`,
          );
        }
        void setCachedAsset(fileName, blob);
        return blob;
      } catch (error) {
        errors.push(error instanceof Error ? error.message : String(error));
        if (attempt < FFMPEG_FETCH_RETRIES - 1) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }
  }

  throw new Error(`Failed to load FFmpeg ${fileName}: ${errors.join("; ")}`);
};

const loadFfmpegAssets = async (
  onLoadProgress?: ExportProgressCallback,
): Promise<{ coreURL: string; wasmURL: string }> => {
  const totalEstimated = FFMPEG_CORE_JS_SIZE + FFMPEG_CORE_WASM_SIZE;
  const bytesByFile: Record<string, number> = {
    "ffmpeg-core.js": 0,
    "ffmpeg-core.wasm": 0,
  };

  const reportCombinedProgress = () => {
    if (!onLoadProgress) return;
    const totalReceived =
      bytesByFile["ffmpeg-core.js"] + bytesByFile["ffmpeg-core.wasm"];
    const percent = Math.min(
      100,
      Math.round((totalReceived / totalEstimated) * 100),
    );
    onLoadProgress(percent);
  };

  const makeByteReporter = (fileName: string) => (received: number) => {
    bytesByFile[fileName] = received;
    reportCombinedProgress();
  };

  const [coreBlob, wasmBlob] = await Promise.all([
    fetchFfmpegAsset(
      "ffmpeg-core.js",
      "text/javascript",
      (received) => makeByteReporter("ffmpeg-core.js")(received),
    ),
    fetchFfmpegAsset(
      "ffmpeg-core.wasm",
      "application/wasm",
      (received) => makeByteReporter("ffmpeg-core.wasm")(received),
    ),
  ]);

  onLoadProgress?.(100);

  return {
    coreURL: blobToObjectURL(coreBlob, "text/javascript"),
    wasmURL: blobToObjectURL(wasmBlob, "application/wasm"),
  };
};

export const getFfmpegInstance = async (
  options?: GetFfmpegInstanceOptions,
): Promise<FFmpeg> => {
  if (ffmpegInstance?.loaded) {
    options?.onLoadProgress?.(100);
    return ffmpegInstance;
  }

  if (options?.onLoadProgress) {
    loadProgressListeners.push(options.onLoadProgress);
  }

  if (ffmpegLoadPromise) return ffmpegLoadPromise;

  ffmpegLoadPromise = (async () => {
    try {
      const ffmpeg = new FFmpeg();
      const { coreURL, wasmURL } = await loadFfmpegAssets(notifyLoadProgress);

      await ffmpeg.load({ coreURL, wasmURL });
      ffmpegInstance = ffmpeg;
      return ffmpeg;
    } catch (error) {
      ffmpegLoadPromise = null;
      ffmpegInstance = null;
      throw error;
    } finally {
      loadProgressListeners = [];
    }
  })();

  return ffmpegLoadPromise;
};

export const warmupFfmpeg = (onProgress?: ExportProgressCallback): void => {
  void getFfmpegInstance({ onLoadProgress: onProgress }).catch(() => undefined);
};

export const readFfmpegFileAsBlob = async (
  ffmpeg: FFmpeg,
  path: string,
  mimeType: string,
): Promise<Blob> => {
  const data = await ffmpeg.readFile(path);
  const bytes =
    data instanceof Uint8Array ? data : new TextEncoder().encode(String(data));
  return new Blob([bytes as BlobPart], { type: mimeType });
};

export const deleteFfmpegFiles = async (
  ffmpeg: FFmpeg,
  paths: string[],
): Promise<void> => {
  await Promise.all(
    paths.map((path) => ffmpeg.deleteFile(path).catch(() => undefined)),
  );
};

export type FfmpegProgressHandle = {
  start: () => void;
  stop: () => void;
};

export const attachFfmpegProgress = (
  ffmpeg: FFmpeg,
  onProgress?: ExportProgressCallback,
  mapProgress?: (rawPercent: number) => number,
): FfmpegProgressHandle => {
  const noop = () => undefined;
  if (!onProgress) return { start: noop, stop: noop };

  let accepting = false;

  const handler = ({ progress }: { progress: number }) => {
    if (!accepting) return;
    const raw = Math.min(100, Math.round(progress * 100));
    onProgress(mapProgress ? mapProgress(raw) : raw);
  };

  ffmpeg.on("progress", handler);

  return {
    start: () => {
      accepting = true;
    },
    stop: () => {
      accepting = false;
      ffmpeg.off("progress", handler);
    },
  };
};

export const inferVideoExtension = (blob: Blob): string => {
  if (blob.type.includes("mp4")) return "mp4";
  if (blob.type.includes("webm")) return "webm";
  if (blob.type.includes("quicktime")) return "mov";
  return "mp4";
};

export const inferAudioExtension = (blob: Blob): string => {
  if (blob.type.includes("wav")) return "wav";
  if (blob.type.includes("webm")) return "webm";
  if (blob.type.includes("mp4") || blob.type.includes("m4a")) return "m4a";
  if (blob.type.includes("ogg")) return "ogg";
  return "webm";
};

export const MP4_VIDEO_ENCODE_ARGS = [
  "-c:v",
  "libx264",
  "-pix_fmt",
  "yuv420p",
  "-profile:v",
  "main",
  "-level",
  "4.0",
  "-crf",
  "20",
  "-preset",
  "ultrafast",
] as const;

export const MP4_AUDIO_ENCODE_ARGS = [
  "-c:a",
  "aac",
  "-b:a",
  "128k",
  "-ar",
  "44100",
  "-ac",
  "2",
] as const;

const writeWavHeader = (
  view: DataView,
  numChannels: number,
  sampleRate: number,
  dataSize: number,
) => {
  const writeString = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) {
      view.setUint8(offset + i, str.charCodeAt(i));
    }
  };

  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");
  writeString(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true);
  view.setUint16(32, numChannels * 2, true);
  view.setUint16(34, 16, true);
  writeString(36, "data");
  view.setUint32(40, dataSize, true);
};

export const audioBlobToWav = async (blob: Blob): Promise<Blob> => {
  const arrayBuffer = await blob.arrayBuffer();
  const audioCtx = new AudioContext();
  await audioCtx.resume();

  try {
    const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
    const numChannels = Math.min(2, audioBuffer.numberOfChannels);
    const sampleRate = audioBuffer.sampleRate;
    const length = audioBuffer.length;
    const dataSize = length * numChannels * 2;
    const wavBuffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(wavBuffer);

    writeWavHeader(view, numChannels, sampleRate, dataSize);

    const channels = Array.from({ length: numChannels }, (_, i) =>
      audioBuffer.getChannelData(i),
    );
    let offset = 44;

    for (let i = 0; i < length; i++) {
      for (let ch = 0; ch < numChannels; ch++) {
        const sample = Math.max(-1, Math.min(1, channels[ch][i] ?? 0));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
        offset += 2;
      }
    }

    return new Blob([wavBuffer], { type: "audio/wav" });
  } finally {
    await audioCtx.close();
  }
};

export const transcodeWebmToMp4 = async (blob: Blob): Promise<Blob> => {
  const ffmpeg = await getFfmpegInstance();
  const inputName = "input.webm";
  const outputName = "output.mp4";

  await ffmpeg.writeFile(inputName, await fetchFile(blob));

  const exitCode = await ffmpeg.exec([
    "-i",
    inputName,
    ...MP4_VIDEO_ENCODE_ARGS,
    ...MP4_AUDIO_ENCODE_ARGS,
    "-movflags",
    "+faststart",
    outputName,
  ]);

  if (exitCode !== 0) {
    throw new Error(`FFmpeg transcode failed with code ${exitCode}`);
  }

  const mp4Blob = await readFfmpegFileAsBlob(ffmpeg, outputName, "video/mp4");
  await deleteFfmpegFiles(ffmpeg, [inputName, outputName]);

  return mp4Blob;
};

export const ensureWhatsAppCompatibleVideo = async (
  blob: Blob,
  mimeType?: string,
): Promise<Blob> => {
  if (isMp4Video(blob, mimeType)) {
    return new Blob([await blob.arrayBuffer()], { type: "video/mp4" });
  }

  return transcodeWebmToMp4(blob);
};
